import React from "react";

export interface Step {
  title: string;
  status?: "completed" | "active" | "disabled";
  icon?: React.ReactNode;
}

interface StepperProps {
  steps: Step[];
  className?: string;
  onStepClick?: (index: number) => void;
  orientation?: "horizontal" | "vertical";
  activeClassName?: string;
  completedClassName?: string;
  disabledClassName?: string;
  theme?: "light" | "dark";
}

/** Check Icon */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function Check({ className = "" }: { className?: string }) {
  return (
  <svg 
    width="24" 
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M17.17 7.85012L10.49 14.5301L6.61 10.6501C6.42264 10.4639 6.16919 10.3593 5.905 10.3593C5.64082 10.3593 5.38737 10.4639 5.2 10.6501C5.01375 10.8375 4.90921 11.0909 4.90921 11.3551C4.90921 11.6193 5.01375 11.8728 5.2 12.0601L9.79 16.6501C9.97737 16.8364 10.2308 16.9409 10.495 16.9409C10.7592 16.9409 11.0126 16.8364 11.2 16.6501L18.58 9.27012C18.6737 9.17716 18.7481 9.06655 18.7989 8.94469C18.8497 8.82284 18.8758 8.69213 18.8758 8.56012C18.8758 8.42811 18.8497 8.2974 18.7989 8.17554C18.7481 8.05368 18.6737 7.94308 18.58 7.85012C18.3926 7.66387 18.1392 7.55933 17.875 7.55933C17.6108 7.55933 17.3574 7.66387 17.17 7.85012Z" fill="currentColor"/>
  </svg>
  );
}

export const GenericStepper: React.FC<StepperProps> = ({
  steps,
  className = "",
  onStepClick,
  orientation = "horizontal",
  theme = "light",
}) => {
  // Theme-based styles
  const baseBackground = theme === "dark" ? "bg-semantic-bg-brand-bold" : "bg-semantic-bg-secondary";
  const baseText = theme === "dark" ? "text-semantic-text-primary" : "text-semantic-text-secondary";
  const borderColor = theme === "dark" ? "border-semantic-bg-brand-bold" : "border-semantic-bg-bg-primary";
  const activeColor =
  theme === "dark"
    ? "bg-brand-bold-secondary text-white"
    : "bg-primary shadow-sm";
  const completedColor = theme === "dark" ? "text-semantic-text-secondary" : "text-semantic-text-primary";
  const disabledColor = theme === "dark" ? "text-semantic-text-secondary" : "text-semantic-text-secondary";

  return (
    <div
      className={`rounded-lg ${baseBackground} border ${borderColor} rounded-md flex ${
        orientation === "vertical" ? "flex-col space-y-4" : ""
      } ${className}`}
    >
      {steps.map((step, index) => {
        const isCompleted = step.status === "completed";
        const isActive = step.status === "active";
        const isDisabled = step.status === "disabled";

        return (
          <div
            key={index}
            className={`flex-1`}
          >
            <button
              disabled={isDisabled}
              onClick={() => !isDisabled && onStepClick?.(index)}
              className={`
                flex items-center justify-center w-full gap-2 px-4 py-2 text-sm rounded-md transition-colors duration-200
                ${isActive ? activeColor : ""}
                ${isCompleted && !isActive ? completedColor : ""}
                ${isDisabled ? disabledColor : ""}
                ${!isActive && !isCompleted && !isDisabled ? baseText : ""}
                ${isDisabled ? "cursor-not-allowed" : "hover:bg-secondary"}
              `}
            >
              {/* Show Check Icon for Completed Steps */}
              {isCompleted && !isActive && (
                <Check className="h-4 w-4 text-semantic-text-secondary" />
              )}

              {/* Step Title Only */}
              <span className="font-medium">{step.title}</span>
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default GenericStepper;
